<template>
  <a-drawer
    placement="right"
    :closable="true"
    :visible="visible"
    width="520"
    class="premium-drawer"
    @close="$emit('update:visible', false)"
  >
    <template #title>
      <div class="drawer-header">
        <div class="header-icon-wrapper">
          <a-icon type="info-circle" theme="filled" class="header-icon" />
        </div>
        <div class="header-text-container">
          <span class="header-title">{{ $t('export_history_page.detail_export') || 'Detail Ekspor Excel' }}</span>
          <span class="header-subtitle">{{ $t('export_history_page.detail_export_subtitle') || 'Excel Export Request Details' }}</span>
        </div>
      </div>
    </template>

    <div class="drawer-content">
      <!-- Section 1: General Info Card -->
      <div class="premium-card">
        <div class="card-header blue">
          <div class="title-icon-wrapper blue">
            <a-icon type="profile" />
          </div>
          <span class="title-text">{{ $t('export_history_page.info') || 'Informasi Ekspor' }}</span>
        </div>
        <div class="card-body">
          <div class="info-rows-container">
            <!-- Workspace -->
            <div class="info-row">
              <div class="info-label">
                <a-icon type="appstore" />
                <span>{{ $t('export_history_page.workspace') || 'Workspace' }}</span>
              </div>
              <div class="info-value">
                <span v-if="data?.workspaces_name" class="workspace-pill">
                  {{ data.workspaces_name }}
                </span>
                <span v-else class="text-muted">-</span>
              </div>
            </div>

            <!-- Business -->
            <div class="info-row">
              <div class="info-label">
                <a-icon type="shop" />
                <span>{{ $t('export_history_page.business') || 'Bisnis' }}</span>
              </div>
              <div class="info-value business-text">
                <span v-if="Array.isArray(data?.businessId) && data.businessId.length">
                  {{ data.businessId.join(', ') }}
                </span>
                <span v-else>-</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 2: Filters & Parameters Card -->
      <div class="premium-card">
        <div class="card-header amber">
          <div class="title-icon-wrapper amber">
            <a-icon type="control" />
          </div>
          <span class="title-text">{{ $t('export_history_page.parameters') || 'Parameter & Filter' }}</span>
        </div>
        <div class="card-body">
          <div class="info-rows-container">
            <!-- Sales Periode -->
            <div class="info-row">
              <div class="info-label">
                <a-icon type="calendar" />
                <span>{{ $t('export_history_page.sales_periode') || 'Periode Pesanan' }}</span>
              </div>
              <div class="info-value">
                <div v-if="data?.dateFrom && data?.dateUntil" class="date-range-badge">
                  <a-icon type="calendar" class="mr-1 text-muted" />
                  <span class="date-text">{{ fmtDate(data.dateFrom) }}</span>
                  <a-icon type="arrow-right" class="mx-2 separator-icon" />
                  <span class="date-text">{{ fmtDate(data.dateUntil) }}</span>
                </div>
                <span v-else class="text-muted">-</span>
              </div>
            </div>

            <!-- Currency -->
            <div class="info-row">
              <div class="info-label">
                <a-icon type="dollar" />
                <span>{{ $t('export_history_page.currency') || 'Mata Uang' }}</span>
              </div>
              <div class="info-value">
                <template v-if="Array.isArray(data?.currencyCode) && data.currencyCode.length">
                  <span v-for="curr in data.currencyCode" :key="curr" class="currency-badge">
                    {{ curr }}
                  </span>
                </template>
                <span v-else class="text-muted">-</span>
              </div>
            </div>

            <!-- Warehouse -->
            <div class="info-row">
              <div class="info-label">
                <a-icon type="bank" />
                <span>{{ $t('export_history_page.warehouse') || 'Gudang' }}</span>
              </div>
              <div class="info-value tags-container">
                <template v-if="Array.isArray(data?.warehouseId) && data.warehouseId.length">
                  <span v-for="wh in data.warehouseId" :key="wh" class="custom-tag wh-tag">
                    <span class="tag-dot orange" />
                    {{ wh }}
                  </span>
                </template>
                <span v-else class="text-muted">-</span>
              </div>
            </div>

            <!-- Sales Channel -->
            <div class="info-row">
              <div class="info-label">
                <a-icon type="shopping" />
                <span>{{ $t('export_history_page.sales_channel') || 'Channel Penjualan' }}</span>
              </div>
              <div class="info-value tags-container">
                <template v-if="Array.isArray(data?.salesChannelId) && data.salesChannelId.length">
                  <span v-for="sc in data.salesChannelId" :key="sc" class="custom-tag sc-tag">
                    <span class="tag-dot cyan" />
                    {{ sc }}
                  </span>
                </template>
                <span v-else class="text-muted">-</span>
              </div>
            </div>

            <!-- Channel / Toko -->
            <div class="info-row">
              <div class="info-label">
                <a-icon type="global" />
                <span>{{ $t('export_history_page.channel') || 'Toko' }}</span>
              </div>
              <div class="info-value tags-container">
                <template v-if="Array.isArray(data?.channelId) && data.channelId.length">
                  <span v-for="ch in data.channelId" :key="ch" class="custom-tag ch-tag">
                    <span class="tag-dot purple" />
                    {{ ch }}
                  </span>
                </template>
                <span v-else class="text-muted">-</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script>
export default {
  name: 'ShowDetailRequest',
  props: {
    visible: { type: Boolean, default: false },
    data: { type: Object, default: () => ({}) },
  },
  methods: {
    fmtDate(iso) {
      if (!iso) return ''
      try {
        if (this.$moment) return this.$moment(iso).format('DD MMM YYYY')
        const d = new Date(iso)
        if (isNaN(d)) return iso
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
      } catch {
        return iso
      }
    },
  },
}
</script>

<style scoped>
/* Drawer styling override to make it premium */
.premium-drawer ::v-deep(.ant-drawer-header) {
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e2e8f0;
  position: relative;
}

.premium-drawer ::v-deep(.ant-drawer-header)::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #2563eb 0%, #07beb8 100%);
}

.premium-drawer ::v-deep(.ant-drawer-body) {
  padding: 16px;
  background-color: #f8fafc;
  min-height: calc(100% - 55px);
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 10px;
  border: 1px solid #bfdbfe;
}

.header-icon {
  font-size: 18px;
  color: #2563eb;
}

.header-text-container {
  display: flex;
  flex-direction: column;
}

.header-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Premium Card Layout */
.premium-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.premium-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.03), 0 4px 6px -4px rgba(0, 0, 0, 0.03);
}

.card-header {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #f1f5f9;
}

.card-header.blue {
  background: linear-gradient(90deg, #f0f7ff 0%, #ffffff 100%);
  border-bottom-color: #e0f2fe;
}

.card-header.amber {
  background: linear-gradient(90deg, #fffbeb 0%, #ffffff 100%);
  border-bottom-color: #fef3c7;
}

.title-text {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.card-body {
  padding: 8px 12px 16px 12px;
}

.title-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 14px;
}

.title-icon-wrapper.blue {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.title-icon-wrapper.amber {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
  border: 1px solid #fcd34d;
}

/* Kiri-Kanan Layout (Row Layout) */
.info-rows-container {
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.info-row:hover {
  background-color: #f8fafc;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.info-label .anticon {
  font-size: 14px;
  color: #94a3b8;
}

.info-value {
  text-align: right;
  font-size: 13px;
  color: #1e293b;
  font-weight: 500;
  max-width: 65%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  word-break: break-word;
}

.business-text {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

/* Modern pill elements */
.workspace-pill {
  background-color: #e0f2fe;
  color: #0284c7;
  padding: 4px 10px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 12px;
  border: 1px solid #bae6fd;
}

.currency-badge {
  background-color: #f1f5f9;
  color: #475569;
  padding: 2px 8px;
  border-radius: 16px;
  font-weight: 700;
  font-size: 11px;
  border: 1px solid #e2e8f0;
}

.date-range-badge {
  display: inline-flex;
  align-items: center;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 4px 10px;
  border-radius: 16px;
  font-weight: 600;
}

.date-text {
  color: #1e293b;
  font-size: 12px;
}

.separator-icon {
  color: #94a3b8;
  font-size: 10px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

/* Modern custom tags to avoid basic style colors */
.custom-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
  line-height: 1.4;
  white-space: nowrap;
}

.tag-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

/* Orange/Amber theme for warehouses */
.wh-tag {
  background-color: #fff7ed;
  border-color: #ffedd5;
  color: #c2410c;
}
.tag-dot.orange {
  background-color: #ea580c;
}

/* Cyan/Teal theme for sales channels */
.sc-tag {
  background-color: #ecfeff;
  border-color: #cffafe;
  color: #0f766e;
}
.tag-dot.cyan {
  background-color: #0d9488;
}

/* Purple theme for stores */
.ch-tag {
  background-color: #faf5ff;
  border-color: #f3e8ff;
  color: #6b21a8;
}
.tag-dot.purple {
  background-color: #8b5cf6;
}

.text-muted {
  color: #94a3b8;
  font-size: 13px;
}
</style>
